export type CrmProgramPreviewFactValue = string | number | boolean | null;

export interface CrmProgramPreviewBoundary {
  rawFactsVisible?: unknown;
  targetWritesExecuted?: unknown;
  servingDecision?: unknown;
  cutoverDecision?: unknown;
}

interface CrmProgramPreviewEvidenceContract extends CrmProgramPreviewBoundary {
  version?: unknown;
  configDigestSha256?: unknown;
  decisionSummary?: unknown;
  matchedQualificationGateReasonCodes?: unknown;
  counts?: unknown;
}

type PreviewFactsValidationResult =
  | { ok: true; value: Record<string, CrmProgramPreviewFactValue> }
  | { ok: false; error: string };

const crmProgramPreviewEvidenceVersion = "stero.crm.program-policy-preview-evidence.v1";
const sha256DigestPattern = /^sha256:[0-9a-f]{64}$/;
const emailLikePattern = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const phoneLikePattern = /(?:\+?\d[\d\s().-]{6,}\d)/;

export function normalizeCrmProgramPreviewFacts(
  value: unknown,
  label = "Preview facts",
): PreviewFactsValidationResult {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ok: false, error: `${label} must be a JSON object.` };
  }

  const entries = Object.entries(value);
  if (entries.length > 100) {
    return { ok: false, error: `${label} must include 100 or fewer fact keys.` };
  }

  const seen = new Set<string>();
  const facts: Record<string, CrmProgramPreviewFactValue> = {};
  for (const [key, factValue] of entries) {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      return { ok: false, error: `${label} contains an empty fact key.` };
    }
    const normalizedKey = trimmedKey.toLowerCase();
    if (seen.has(normalizedKey)) {
      return { ok: false, error: `${label} contains duplicate fact key '${trimmedKey}'.` };
    }
    seen.add(normalizedKey);

    if (isUnsafePreviewFactKey(trimmedKey)) {
      return {
        ok: false,
        error: `${label} key '${trimmedKey}' is not allowed. Use non-PII aggregate signal keys only.`,
      };
    }

    const normalizedValue = normalizePreviewFactValue(factValue, `${label} fact '${trimmedKey}'`);
    if (!normalizedValue.ok) return normalizedValue;
    facts[trimmedKey] = normalizedValue.value;
  }

  return { ok: true, value: facts };
}

export function validateCrmProgramPreviewBoundary(
  value: unknown,
  label = "CRM policy preview response",
): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return `${label} must be an object with explicit safety boundaries.`;
  }
  const boundary = value as CrmProgramPreviewBoundary;
  if (boundary.rawFactsVisible !== false) return `${label} must not expose raw facts.`;
  if (boundary.targetWritesExecuted !== false) return `${label} must not execute target writes.`;
  if (boundary.servingDecision !== "NOT_READY_FOR_SERVING") return `${label} must keep serving blocked.`;
  if (boundary.cutoverDecision !== "NOT_READY_FOR_CUTOVER") return `${label} must keep cutover blocked.`;
  return null;
}

export function validateCrmProgramPreviewEvidenceContract(
  value: unknown,
  label = "CRM policy preview evidence",
  expectedConfigDigestSha256?: string,
): string | null {
  const boundaryError = validateCrmProgramPreviewBoundary(value, label);
  if (boundaryError) return boundaryError;

  const evidence = value as CrmProgramPreviewEvidenceContract;
  if (evidence.version !== crmProgramPreviewEvidenceVersion) {
    return `${label} version must equal ${crmProgramPreviewEvidenceVersion}.`;
  }
  if (typeof evidence.configDigestSha256 !== "string" || !sha256DigestPattern.test(evidence.configDigestSha256)) {
    return `${label} must include a valid policy config digest.`;
  }
  if (expectedConfigDigestSha256 && evidence.configDigestSha256 !== expectedConfigDigestSha256) {
    return `${label} policy config digest must match the preview response.`;
  }

  if (!evidence.decisionSummary || typeof evidence.decisionSummary !== "object" || Array.isArray(evidence.decisionSummary)) {
    return `${label} must include a decision summary.`;
  }
  const decisionSummary = evidence.decisionSummary as {
    scoreBeforeQualification?: unknown;
    qualificationGate?: unknown;
  };
  if (!Number.isFinite(decisionSummary.scoreBeforeQualification)) {
    return `${label} must include scoreBeforeQualification.`;
  }
  if (typeof decisionSummary.qualificationGate !== "string" || decisionSummary.qualificationGate.trim() === "") {
    return `${label} must include qualificationGate.`;
  }

  if (!evidence.counts || typeof evidence.counts !== "object" || Array.isArray(evidence.counts)) {
    return `${label} must include aggregate counts.`;
  }
  const counts = evidence.counts as {
    matchedQualificationGates?: unknown;
    matchedQualificationGateReasonCodes?: unknown;
  };
  if (!Number.isFinite(counts.matchedQualificationGates)) {
    return `${label} must include matchedQualificationGates count.`;
  }
  if (!Array.isArray(evidence.matchedQualificationGateReasonCodes)) {
    return `${label} must include matchedQualificationGateReasonCodes.`;
  }
  if (!Number.isFinite(counts.matchedQualificationGateReasonCodes)) {
    return `${label} must include matchedQualificationGateReasonCodes count.`;
  }
  const capStatus = decisionSummary.qualificationGate === "soft_cap" || decisionSummary.qualificationGate === "hard_cap";
  if (
    capStatus &&
    (counts.matchedQualificationGates as number) > 0 &&
    evidence.matchedQualificationGateReasonCodes.length === 0
  ) {
    return `${label} cap qualification evidence must include at least one matched gate reason code.`;
  }

  return null;
}

function normalizePreviewFactValue(
  value: unknown,
  label: string,
): { ok: true; value: CrmProgramPreviewFactValue } | { ok: false; error: string } {
  if (typeof value === "number") {
    return Number.isFinite(value)
      ? { ok: true, value }
      : { ok: false, error: `${label} must be a finite number.` };
  }

  if (typeof value === "boolean" || value === null) {
    return { ok: true, value };
  }

  if (typeof value === "string") {
    if (value.length > 160) {
      return { ok: false, error: `${label} must be 160 characters or fewer.` };
    }
    if (emailLikePattern.test(value) || phoneLikePattern.test(value) || isUnsafePreviewFactValue(value)) {
      return {
        ok: false,
        error: `${label} looks private. Use counts, booleans, score bands, or sanitized labels instead.`,
      };
    }
    return { ok: true, value };
  }

  return { ok: false, error: `${label} must be a string, number, boolean, or null.` };
}

const alwaysUnsafeFactTokens = new Set([
  "private",
  "secret",
  "token",
  "password",
  "raw",
  "payload",
]);

const contentFactTokens = new Set([
  "address",
  "body",
  "comment",
  "comments",
  "message",
  "messages",
  "note",
  "notes",
  "text",
  "transcript",
]);

const identifierFactTokens = new Set([
  "email",
  "e-mail",
  "handle",
  "mobile",
  "name",
  "phone",
  "username",
]);

const channelFactTokens = new Set(["telegram", "whatsapp"]);
const channelIdentifierFactTokens = new Set(["id", "identifier", "number"]);

const aggregateFactTokens = new Set([
  "attempt",
  "attempted",
  "attempts",
  "band",
  "booked",
  "booking",
  "bookings",
  "bounce",
  "bounces",
  "bucket",
  "buckets",
  "call",
  "calls",
  "class",
  "classes",
  "click",
  "clicks",
  "completed",
  "completion",
  "completions",
  "complaint",
  "complaints",
  "count",
  "counts",
  "days",
  "engagement",
  "event",
  "events",
  "flag",
  "hours",
  "is",
  "minutes",
  "open",
  "opens",
  "practice",
  "rate",
  "rates",
  "ready",
  "replied",
  "replies",
  "reply",
  "request",
  "requested",
  "requests",
  "score",
  "signal",
  "signals",
  "stage",
  "status",
  "streak",
  "submission",
  "submissions",
  "submitted",
  "total",
  "totals",
  "unsubscribed",
  "unsubscribes",
  "usage",
  "view",
  "views",
  "visit",
  "visits",
]);

function isUnsafePreviewFactKey(value: string): boolean {
  const tokens = tokenizePreviewFactText(value);
  if (tokens.some((token) => alwaysUnsafeFactTokens.has(token))) return true;

  const hasAggregateContext = tokens.some((token) => aggregateFactTokens.has(token));
  if (tokens.some((token) => contentFactTokens.has(token))) return !hasAggregateContext;
  if (tokens.some((token) => identifierFactTokens.has(token))) return !hasAggregateContext;

  return tokens.some((token) => channelFactTokens.has(token))
    && tokens.some((token) =>
      identifierFactTokens.has(token) || contentFactTokens.has(token) || channelIdentifierFactTokens.has(token))
    && !hasAggregateContext;
}

function isUnsafePreviewFactValue(value: string): boolean {
  const tokens = tokenizePreviewFactText(value);
  if (tokens.some((token) => alwaysUnsafeFactTokens.has(token))) return true;
  const hasAggregateContext = tokens.some((token) => aggregateFactTokens.has(token));
  return tokens.some((token) => contentFactTokens.has(token)) && !hasAggregateContext;
}

function tokenizePreviewFactText(value: string): string[] {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[^A-Za-z0-9-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.toLowerCase());
}
